import React, { useState, useEffect } from 'react';
import { Moon, X, Phone, Mail, User, Loader2, CheckCircle } from 'lucide-react';

const FORMSPREE_ID = 'mvkpjgoe';

const FloatingCTA = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '' });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Lead — Floating Button',
          'Lead Type': 'Sleep Apnea Quick Contact',
          'First Name': formData.firstName,
          'Email': formData.email,
          'Phone': formData.phone,
          'Source Page': window.location.pathname,
        }),
      });
      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-20 right-4 z-40" data-testid="floating-cta">
      {/* Expanded Form */}
      {isOpen && (
        <div className="mb-3 bg-white rounded-2xl shadow-2xl border border-gray-200 w-80 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="floating-form">
          <div className="bg-cyan-700 px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-white font-bold text-sm">Sleep Apnea Help</p>
              <p className="text-cyan-200 text-xs">Get a free consultation</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-cyan-200 hover:text-white">
              <X size={18} />
            </button>
          </div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="p-5 space-y-3">
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  required
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  data-testid="floating-firstname"
                />
              </div>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  required
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  data-testid="floating-email"
                />
              </div>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  data-testid="floating-phone"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                data-testid="floating-submit"
              >
                {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Get Free Consultation'}
              </button>
              <p className="text-xs text-gray-400 text-center">No spam. We respect your privacy.</p>
            </form>
          ) : (
            <div className="p-6 text-center" data-testid="floating-success">
              <CheckCircle size={36} className="mx-auto text-green-500 mb-3" />
              <p className="font-bold text-gray-900 mb-1">Thank you!</p>
              <p className="text-gray-500 text-sm">We'll contact you shortly.</p>
            </div>
          )}
        </div>
      )}

      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 pl-4 pr-5 py-3 group"
          data-testid="floating-button"
        >
          <Moon size={20} className="group-hover:animate-pulse" />
          <span className="font-semibold text-sm whitespace-nowrap">Sleep Apnea?</span>
        </button>
      )}
    </div>
  );
};

export default FloatingCTA;
